import React, { Suspense, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { SidePanelType } from 'features/side-panels/types';
import { PanelTypeProvider } from 'features/side-panels/PanelTypeContext';
import { getPanelStyles } from 'features/side-panels/getPanelStyles';

const PanelPlaceholder = (
  <Box
    sx={{
      height: '100%',
      bgcolor: 'background.paper',
      borderRadius: '8px',
      overflow: 'hidden',
      border: 1,
      borderColor: 'divider',
    }}
  />
);

const ErrorFallback = () => {
  const { t } = useTranslation();

  return (
    <Typography
      sx={{
        p: 2,
      }}
    >
      {t('generic_error_short')}
    </Typography>
  );
};

const PanelLoaded = ({
  type,
  onLoaded,
  children,
}: {
  type: SidePanelType;
  onLoaded: (type: SidePanelType) => void;
  children: React.ReactElement;
}) => {
  useEffect(() => {
    onLoaded(type);
  }, [type, onLoaded]);

  return children;
};

type PanelSlotProps = {
  type: SidePanelType;
  Panel: React.ComponentType;
  pinnedPanels: SidePanelType[];
  activePanel: SidePanelType | null;
  exhausted: boolean;
  onError: (type: SidePanelType) => void;
  onLoaded: (type: SidePanelType) => void;
};

export const PanelSlot = ({
  type,
  Panel,
  pinnedPanels,
  activePanel,
  exhausted,
  onError,
  onLoaded,
}: PanelSlotProps) => (
  <Box sx={getPanelStyles({ type, pinnedPanels, activePanel })}>
    <Sentry.ErrorBoundary
      fallback={exhausted ? <ErrorFallback /> : PanelPlaceholder}
      onError={() => onError(type)}
    >
      <Suspense fallback={PanelPlaceholder}>
        <PanelLoaded type={type} onLoaded={onLoaded}>
          <PanelTypeProvider value={type}>
            <Panel />
          </PanelTypeProvider>
        </PanelLoaded>
      </Suspense>
    </Sentry.ErrorBoundary>
  </Box>
);
